import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Tags } from '@shared/components/ui/Tags';
import { cn } from '@shared/utils/utils';
import { useCountries } from '../../hooks/useCountries';

interface ImportFiltersProps {
  include: Record<string, string[]>;
  exclude: Record<string, string[]>;
  onChange: (type: 'include' | 'exclude', field: string, values: string[]) => void;
  onRemoveField: (type: 'include' | 'exclude', field: string) => void;
  highlightedField?: string | null;
}

const ALLOWED_FIELDS = [
  'country',
  'payment_method',
  'shipping_method',
  'order_status',
  'currency',
  'tags',
  'email',
  'zip',
  'city',
  'province',
  'courier',
  'tracking_number',
  'external_financial_status',
  'fulfillment',
];

const STATIC_OPTIONS: Record<string, { label: string; value: string }[]> = {
  currency: ['EUR', 'USD', 'MXN', 'CLP', 'COP', 'BRL', 'PEN', 'ARS', 'GBP'].map((c) => ({ label: c, value: c })),
  order_status: [
    { label: 'Pagado (paid)', value: 'paid' },
    { label: 'Pendiente (pending)', value: 'pending' },
    { label: 'Autorizado (authorized)', value: 'authorized' },
    { label: 'Pago parcial (partially_paid)', value: 'partially_paid' },
    { label: 'Anulado (voided)', value: 'voided' },
  ],
  external_financial_status: [
    { label: 'Pagado (paid)', value: 'paid' },
    { label: 'Pendiente (pending)', value: 'pending' },
    { label: 'Autorizado (authorized)', value: 'authorized' },
    { label: 'Reembolsado (refunded)', value: 'refunded' },
  ],
};

export const ImportFilters = ({ include, exclude, onChange, onRemoveField, highlightedField }: ImportFiltersProps) => {
  const { t } = useTranslation('shopsModule');
  const { countries } = useCountries();
  const [newIncludeField, setNewIncludeField] = useState('');
  const [newExcludeField, setNewExcludeField] = useState('');
  const [tagInputs, setTagInputs] = useState<Record<string, string>>({});
  const [expandedField, setExpandedField] = useState<string | null>(null);

  const handleAddValue = (type: 'include' | 'exclude', field: string, value: string) => {
    const trimmedValue = value?.trim();
    if (trimmedValue) {
      const currentValues = type === 'include' ? include[field] : exclude[field];
      if (!currentValues.includes(trimmedValue)) {
        onChange(type, field, [...currentValues, trimmedValue]);
      }
      setTagInputs((prev) => ({ ...prev, [`${type}-${field}`]: '' }));
      setExpandedField(null);
    }
  };

  const handleRemoveTag = (type: 'include' | 'exclude', field: string, tag: string) => {
    const currentValues = type === 'include' ? include[field] : exclude[field];
    onChange(
      type,
      field,
      currentValues.filter((t) => t !== tag),
    );
  };

  const renderFilterBlock = (type: 'include' | 'exclude', data: Record<string, string[]>) => {
    const isInclude = type === 'include';

    return (
      <div
        className={cn(
          'flex-1 p-4 rounded-xl border border-dashed',
          isInclude ? 'border-success/30 bg-success/5' : 'border-error/30 bg-error/5',
        )}
      >
        <h3
          className={cn(
            'text-sm font-semibold mb-4 flex items-center gap-2',
            isInclude ? 'text-success' : 'text-error',
          )}
        >
          {isInclude ? t('modals.settings.importFilters.include') : t('modals.settings.importFilters.exclude')}
        </h3>

        <div className="flex gap-2 mb-4">
          <Select
            value={isInclude ? newIncludeField : newExcludeField}
            onValueChange={(value) => {
              if (value && !include[value] && !exclude[value]) {
                onChange(type, value, []);
                setTimeout(() => {
                  setExpandedField(`${type}-${value}`);
                }, 50);
              }
              if (isInclude) setNewIncludeField('');
              else setNewExcludeField('');
            }}
          >
            <SelectTrigger
              className={cn(
                'bg-card-bg border-lines focus:ring-0 focus:ring-offset-0',
                highlightedField && 'opacity-50',
              )}
            >
              <SelectValue placeholder={t('modals.settings.importFilters.addField')} />
            </SelectTrigger>
            <SelectContent>
              {ALLOWED_FIELDS.filter((f) => !include[f] && !exclude[f]).map((field) => (
                <SelectItem key={field} value={field}>
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{t(`modals.settings.importFilters.fieldLabels.${field}`)}</span>
                    <span className="text-[10px] opacity-50 font-normal">
                      {t(`modals.settings.importFilters.fieldDescriptions.${field}`)}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {Object.entries(data).map(([field, values]) => {
            const isHighlighted = highlightedField === `${type}-${field}`;
            const isExpanded = expandedField === `${type}-${field}`;

            return (
              <div
                key={field}
                className={cn(
                  'p-3 rounded-lg border transition-all duration-300 group relative bg-body-bg/40',
                  isHighlighted ? 'border-title shadow-none bg-bg-success' : 'border-card-border',
                )}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">
                        {t(`modals.settings.importFilters.fieldLabels.${field}`)}
                      </span>
                      <p className="text-[10px] text-complementary-text mt-0.5 leading-tight">
                        {t(`modals.settings.importFilters.fieldDescriptions.${field}`)}
                      </p>
                    </div>
                    {isHighlighted && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-tighter border bg-success/10 text-success border-success/20">
                        {t('common.editing')}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveField(type, field)}
                    className="text-text-secondary hover:text-error transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5 items-center">
                  {values.map((val) => {
                    let displayLabel = val;
                    if (STATIC_OPTIONS[field]) {
                      const opt = STATIC_OPTIONS[field].find((o) => o.value === val);
                      if (opt) displayLabel = opt.label;
                    }

                    return (
                      <Tags key={val} onRemove={() => handleRemoveTag(type, field, val)} className="bg-card-border">
                        {displayLabel}
                      </Tags>
                    );
                  })}

                  {isExpanded ? (
                    <div className="flex items-center">
                      {field === 'country' ? (
                        <Combobox
                          options={countries.map((c) => ({ label: c.name, value: c.iso2 }))}
                          placeholder={t('modals.settings.importFilters.selectCountry', 'Seleccionar país')}
                          value=""
                          setValue={(val) => handleAddValue(type, field, val)}
                          className="w-[180px] text-xs h-8"
                        />
                      ) : STATIC_OPTIONS[field] ? (
                        <Select onValueChange={(val) => handleAddValue(type, field, val)}>
                          <SelectTrigger className="h-8 w-[180px] text-xs bg-transparent border-lines">
                            <SelectValue
                              placeholder={t('modals.settings.importFilters.selectOption', 'Seleccionar opción')}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {STATIC_OPTIONS[field].map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          autoFocus
                          placeholder={t('modals.settings.importFilters.valuePlaceholder')}
                          value={tagInputs[`${type}-${field}`] || ''}
                          onChange={(e) => setTagInputs((prev) => ({ ...prev, [`${type}-${field}`]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddValue(type, field, tagInputs[`${type}-${field}`]);
                            }
                            if (e.key === 'Escape') {
                              setExpandedField(null);
                            }
                          }}
                          onBlur={() => {
                            if (!tagInputs[`${type}-${field}`]) {
                              setExpandedField(null);
                            }
                          }}
                          className={cn(
                            'h-8 rounded-full text-xs px-3 bg-transparent w-[140px]',
                            isInclude ? 'border-success/30' : 'border-error/30',
                          )}
                        />
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 rounded-full bg-card-border border-0 !text-icons hover:bg-card-border/80 p-0"
                      onClick={() => setExpandedField(`${type}-${field}`)}
                    >
                      <FontAwesomeIcon icon={faPlus} className="w-2.5 h-2.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {Object.keys(data).length === 0 && (
            <p className="text-xs text-text-secondary text-center py-4 italic">
              {t('modals.settings.importFilters.noFilters')}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-text-secondary">{t('modals.settings.importFilters.description')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {renderFilterBlock('include', include)}
        {renderFilterBlock('exclude', exclude)}
      </div>
    </div>
  );
};
